import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { getBackendUrl, isDevelopment, logApiError } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
import { useUrlPrefill } from './hooks/useUrlPrefill';
import { useDrawerHeightTransition } from './hooks/useDrawerHeightTransition';
import {
  BrandColorProvider,
  LEGACY_UNBRANDED_FORM_COLOR,
  brandPaletteFor,
  useBrandColors,
  safeFetch,
  safeFetchJSON,
  markJourney,
  JOURNEY_MARKS,
} from '@charitystack/shared';
import { resolveElementsConfigUrl, resolveFormPage } from './Util/embedFormConfig';
import { readElementsConfig, rememberElementsConfig } from './Util/elementsConfigCache';
import { DYNAMIC_FORM_MIN_HEIGHT } from './Util/formHeightUtils';
import EmbedFormDetailPage from './EmbedFormDetailPage';
import StandardForm from './StandardForm';
import { getLoadedFormType, isLazyFormType, loadFormType } from './Util/formTypeModules';
import FormSkeleton from './Util/FormSkeleton';
import { buildGroupTicketV2ElementsConfigUrl } from './Util/groupTicketContract';
import { DisplayCurrencyProvider } from './currency/DisplayCurrencyProvider';
import FormSurfacePage from './Components/FormSurfacePage';

const serializeConfigForComparison = (config) =>
  JSON.stringify(config, (_key, value) => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;

    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, value[key]]),
    );
  });

/**
 * EmbedForm Component
 *
 * Routes to the correct form component (StandardForm, CrowdfundingForm, ProductForm,
 * TicketSelectionForm) based on formType from the API response.
 *
 * formDisplayMode prop controls sizing and UI behavior:
 *
 *   formDisplayMode = 'dynamic'          formDisplayMode = 'overlay'
 *   (embed, hosted, drawers)             (desktop overlay only)
 *   ┌─────────────────────────┐          ┌─────────────────────────┐
 *   │ DonationFrequencies:    │          │ DonationFrequencies:    │
 *   │   Button bar (main)     │          │   Once/Repeat + dropdown│
 *   │ ProductForm:            │          │ ProductForm:            │
 *   │   Single page, inline   │          │   Two-page flow         │
 *   │ GivingLevels:           │          │ GivingLevels:           │
 *   │   Visible descriptions  │          │   Tooltip descriptions  │
 *   │ ScheduledDonations:     │          │ ScheduledDonations:     │
 *   │   Text wraps if narrow  │          │   Truncated text        │
 *   └─────────────────────────┘          └─────────────────────────┘
 *
 * Also supports:
 * - PREVIEW MODE (previewMode=true): Uses provided previewData for dashboard preview
 * - Detail page (EmbedFormDetailPage): Shown in embed mode, skipped in overlay mode
 * - Carrying a selection (CON-336, standard form only): `onSelectionChange` reports the
 *   donor's { amount, frequency, fund }; `carriedSelection` opens a fresh mount on one. Only
 *   the hosted page passes them, so its drawer copy opens on the desktop copy's pick.
 */
function EmbedForm({
  formID,
  isDrawerOpen,
  brandColors,
  backendUrl: propBackendUrl,
  previewMode = false,
  hostedPagePreview = false,
  previewData = null,
  onReady,
  formDisplayMode = 'dynamic',
  elementPrefills = null,
  hideFAQ = false,
  headingLevel = 'h1',
  mountBrandProvider = false,
  paymentsEnabled,
  carriedSelection = null,
  onSelectionChange,
  onPreviewSubmit,
}) {
  const isHostedPage = useIsHostedPage();

  // The url this mount will read, decided before the first render so the seed
  // below and `fetchElementsConfig` can never disagree about which payload is
  // this mount's (CON-21).
  const configUrl = useMemo(
    () =>
      resolveElementsConfigUrl({
        formID,
        previewMode,
        previewData,
        formDisplayMode,
        backendUrl: propBackendUrl,
      }),
    [formID, previewMode, previewData, formDisplayMode, propBackendUrl],
  );

  // A payload a sibling widget on this page already resolved, if there is one.
  //
  // This is the anchor case's fix: on a phone the hosted page's desktop rail
  // holds a mounted widget that fetched the configuration at page load, and the
  // drawer's widget used to ask the network for it again and show a placeholder
  // for the whole round trip. Reading the sibling's answer lets the drawer open
  // on the form instead. Deliberately NOT used in preview mode — the dashboard
  // previewer composes `previewData` over the fetched payload and its first
  // frame is a different question from a donor's.
  //
  // A seeded mount STILL READS the network — the seed decides its first frame,
  // not its configuration. What it must not do is hand the forms a new object
  // identity for a payload they already have: every form re-initialises from
  // `props.data`, resetting the amount, the frequency, the fund and every
  // custom-input answer. So the response is applied only when it DIFFERS from
  // the seed. See `fetchElementsConfig` below.
  const seededConfig = useMemo(
    () => (previewMode ? null : readElementsConfig(configUrl)),
    // The seed is a first-frame decision, so it is read once per config identity
    // and never re-read on a later render of the same mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configUrl, previewMode],
  );

  const [data, setConfigData] = useState(seededConfig);
  const [loadedForm, setLoadedForm] = useState(() => {
    if (isLazyFormType(seededConfig?.formType)) {
      // Start on the first render, not after the first committed placeholder.
      // The effect below owns reporting errors for the current configuration.
      loadFormType(seededConfig.formType, previewMode).catch(() => {});
    }
    return { type: seededConfig?.formType, component: getLoadedFormType(seededConfig?.formType) };
  });
  const FormTypeComponent = getLoadedFormType(data?.formType) ||
    (loadedForm.type === data?.formType ? loadedForm.component : null);
  const formModuleReady = !isLazyFormType(data?.formType) || Boolean(FormTypeComponent);
  const setData = (config) => {
    if (isLazyFormType(config?.formType)) loadFormType(config.formType, previewMode).catch(() => {});
    setConfigData(config);
  };
  const [currentPage, setCurrentPage] = useState(() =>
    // Without a seed this keeps the historical 'DETAIL_PAGE' default, which is
    // unobservable behind the placeholder and is corrected by the effect below
    // once the response lands. With one, the page is already decidable, and
    // deciding it here is what lets the first frame be the real form (CON-21).
    seededConfig
      ? resolveFormPage({
          data: seededConfig,
          isHostedPage,
          hostedPagePreview,
          formDisplayMode,
          isDrawerOpen,
        })
      : 'DETAIL_PAGE',
  );
  const [loadError, setLoadError] = useState(false);
  // Same-type refreshes cannot recover a failed chunk; a different type no longer needs it.
  const [failedFormType, setFailedFormType] = useState(null);
  const formModuleError = failedFormType !== null && failedFormType === data?.formType;
  const showLoadError = formModuleError || (loadError && !previewMode);
  useEffect(() => {
    const type = data?.formType;
    setFailedFormType((failedType) => failedType === type ? failedType : null);
    if (!isLazyFormType(type)) return;
    let active = true;
    loadFormType(type, previewMode).then(
      (component) => { if (active) setLoadedForm({ type, component }); },
      () => { if (active) setFailedFormType(type); },
    );
    return () => { active = false; };
  }, [data?.formType, previewMode]);
  const [pageDirection, setPageDirection] = useState(null);
  const [elementID, setElementID] = useState('');
  const [domID, setDomID] = useState('');
  // Flag to prevent flicker - only show content when page is determined.
  // A seeded mount has already determined it, one line above.
  const [isReady, setIsReady] = useState(Boolean(seededConfig));
  const [showContent, setShowContent] = useState(Boolean(seededConfig)); // Flag to trigger CSS fade-in animation
  const [backendDataLoaded, setBackendDataLoaded] = useState(false); // Track if backend fetch completed
  const hookUrlOverrides = useUrlPrefill();
  // Merge element prefills (from URL element config) with URL overrides.
  // URL params take precedence over element prefills.
  const urlOverrides = elementPrefills
    ? { ...elementPrefills, ...hookUrlOverrides }
    : hookUrlOverrides;
  // CON-336: the donor's live amount, frequency and fund from the hosted page's other copy of
  // this form, carried into a fresh mount. It is merged LAST on purpose: a pick the donor
  // made on this page beats the page's `?amount=` / `?frequency=` / `?fund=`, which the other
  // copy already applied before the donor changed it. Standard form only — the other form
  // types never report one.
  const standardFormOverrides = carriedSelection
    ? { ...urlOverrides, ...carriedSelection }
    : urlOverrides;
  // Pre-generate brand colors at EmbedForm level to ensure they're ready before child forms render
  // This prevents the flicker caused by useBrandColors initializing with empty state
  // CRITICAL: Ensure brandColors is a hex string, not an object - brandColors prop might be either
  const brandColorHex = typeof brandColors === 'string' ? brandColors : null;
  const configuredBrandColor =
    data?.lightColor || previewData?.lightColor || previewData?.color || brandColorHex || null;
  // CON-528: no configured colour → the legacy "no colour" value, which resolves to the
  // neutral palette and goes on DONATE as unbranded (CON-331's route), never sage.
  const formColor = configuredBrandColor || LEGACY_UNBRANDED_FORM_COLOR;
  const { brandColors: hookBrandColors } = useBrandColors(formColor);
  // `useBrandColors` fills its palette in an effect, so its FIRST render is
  // always empty and `areBrandColorsReady` below is always false for one frame.
  // That is the gate this component uses to hold the placeholder, so a mount
  // that already has its configuration would still have had to paint the
  // placeholder once, for no reason (CON-21). `brandPaletteFor` is the pure
  // function the hook itself calls, on the same input, so this resolves the same
  // palette a frame earlier and the hook's copy takes over unchanged after it.
  // (It is `generateBrandColors` for every colour except the legacy "no colour"
  // placeholder, which resolves to the neutral donor set — CON-331.)
  const generatedBrandColors = useMemo(
    () =>
      hookBrandColors && Object.keys(hookBrandColors).length > 0
        ? hookBrandColors
        : brandPaletteFor(formColor),
    [hookBrandColors, formColor],
  );
  const areBrandColorsReady = generatedBrandColors && Object.keys(generatedBrandColors).length > 0;

  // Track if user has manually navigated (to prevent unwanted resets in preview mode)
  const hasUserNavigatedRef = useRef(false);
  // Track the previous enableEmbedForm value to detect changes
  const previousEnableEmbedFormRef = useRef(null);
  // Track if onReady callback has been called to prevent multiple calls
  const onReadyCalledRef = useRef(false);
  // CON-63 mark 1 fires once per mounted form; this is its guard.
  const formVisibleMarkedRef = useRef(false);
  // Track if preview mode retry has been attempted (to prevent infinite retry loops)
  const previewModeRetryAttemptedRef = useRef(false);
  // A response from an earlier form must not replace the form now mounted here.
  // The request-local `formID` below is a closed-over value, so comparing it to
  // another local copy can never detect a prop change.
  const activeConfigRef = useRef({ formID, configUrl, generation: 0 });
  const configRequestRef = useRef(0);
  const lastAppliedConfigRef = useRef(null);
  useLayoutEffect(() => {
    if (activeConfigRef.current.formID !== formID || activeConfigRef.current.configUrl !== configUrl) {
      activeConfigRef.current = {
        formID,
        configUrl,
        generation: activeConfigRef.current.generation + 1,
      };
    }
  }, [formID, configUrl]);
  // The url this mount opened on a seed for, or null. Keyed on the url rather
  // than being a one-shot flag on purpose: React's development double-invoke
  // runs the configuration effect twice, and a flag consumed by the first run
  // would let the second one repaint the very placeholder the seed removes
  // (CON-21).
  const seededConfigUrlRef = useRef(seededConfig ? configUrl : null);
  // What this mount most recently handed the forms, serialized BEFORE they
  // mutate it — `StandardForm` writes `input.answers` onto the payload and
  // normalises its amount arrays in place, so the live object cannot be compared
  // against a fresh response. A cold mount starts at the no-payload sentinel and
  // records its first successful response before any overlapping refresh lands.
  const renderedPayloadRef = useRef(
    seededConfig ? serializeConfigForComparison(seededConfig) : null,
  );

  const heightTransitionEnabled = isDrawerOpen || formDisplayMode === 'dynamic';
  const { containerRef: drawerContainerRef, prepareTransition } =
    useDrawerHeightTransition(heightTransitionEnabled);

  // Whether this mount renders the detail page at all. An overlay shows that
  // content in its left pane, a drawer has no room for it, and a hosted page (or
  // its preview) shows it in the LeftPanel beside the form. On the hosted page it
  // used to be mounted anyway, hidden: `resolveFormPage` never opens it there,
  // but its FundraisingBar and FAQ panel still read the network after the form
  // configuration had settled — two GETs nobody saw (CON-75).
  const rendersDetailPage =
    formDisplayMode !== 'overlay' && !isDrawerOpen && !isHostedPage && !hostedPagePreview;

  // Page transition function - CSS handles the crossfade, hook handles drawer height
  const smoothSetCurrentPage = (targetPage, direction = null) => {
    // Never navigate to a page this mount does not render — the form would be
    // replaced by nothing (the drawer's A11-003, generalised).
    if (targetPage === 'DETAIL_PAGE' && !rendersDetailPage) return;
    // Mark that user has manually navigated (e.g., clicked "Support" button)
    if (previewMode) {
      hasUserNavigatedRef.current = true;
    }
    prepareTransition();
    setPageDirection(direction);
    setCurrentPage(targetPage);
  };

  /**
   * Merges preview data with fetched backend data
   * Preview data contains user-editable fields, while backend data contains full config
   * @param {Object} fetchedData - Complete data from backend API
   * @param {Object} previewData - Partial data from preview/edit mode
   * @returns {Object} Merged data object
   */
  const mergePreviewWithFetchedData = (fetchedData, previewData) => {
    if (!previewData || !fetchedData) {
      return fetchedData || previewData || null;
    }

    // Create merged object starting with fetched data (complete object)
    const merged = { ...fetchedData };

    // Map of preview field names to backend field names (if different)
    const fieldMapping = {
      color: 'lightColor', // preview uses 'color', backend uses 'lightColor'
      convertedContent: 'eventDetails', // preview uses 'convertedContent' for events
      customFAQs: 'faqs', // preview might use customFAQs
      customTickets: 'tickets', // preview uses customTickets
      merchantName: 'nonprofitName', // preview uses merchantName
    };

    // Override with preview data fields
    Object.keys(previewData).forEach((key) => {
      const value = previewData[key];

      // Skip undefined values - we want to keep backend values for these
      if (value === undefined) {
        return;
      }

      // Check if there's a field mapping
      const targetKey = fieldMapping[key] || key;

      // Update the merged object with preview value
      merged[targetKey] = value;

      // For color field, update both lightColor and darkColor
      if (key === 'color') {
        merged.lightColor = value;
        merged.darkColor = value;
      }

      // Handle description fields based on form type
      // For EMBED_FORM: use 'details' field
      // For EVENT: 'convertedContent' maps to 'eventDetails'
      if (key === 'details') {
        merged.details = value;
      }

      if (key === 'convertedContent') {
        if (merged.formType === 'EVENT') {
          merged.eventDetails = value;
          merged.details = value; // Also set details for consistency
        } else {
          // For non-EVENT forms, convertedContent should map to details
          merged.details = value;
        }
      }

      // Handle customFAQs mapping to faqs
      if (key === 'customFAQs') {
        merged.faqs = value;
      }

      // Handle customTickets mapping to tickets
      if (key === 'customTickets') {
        merged.tickets = value;
      }

      // Handle merchantName mapping to nonprofitName
      if (key === 'merchantName') {
        merged.nonprofitName = value;
      }
    });

    // Ensure amountOffset and countOffset are numbers (not strings)
    if (merged.amountOffset && typeof merged.amountOffset === 'string') {
      merged.amountOffset = parseInt(merged.amountOffset, 10) || 0;
    }
    if (merged.countOffset && typeof merged.countOffset === 'string') {
      merged.countOffset = parseInt(merged.countOffset, 10) || 0;
    }
    if (merged.targetAmount && typeof merged.targetAmount === 'string') {
      merged.targetAmount = parseInt(merged.targetAmount, 10) || 0;
    }

    // CRITICAL: Handle showScheduleDonation properly
    // Default to false only if not explicitly set (undefined or null)
    if (merged.showScheduleDonation === undefined || merged.showScheduleDonation === null) {
      merged.showScheduleDonation = false;
      // Clear any preset scheduled donation data when defaulting to false
      if (merged.defaultScheduledRange) {
        merged.defaultScheduledRange = {
          startDate: null,
          endDate: null,
          scheduledTime: null,
        };
      }
    } else if (merged.showScheduleDonation === false) {
      // If explicitly set to false, clear scheduled donation data
      if (merged.defaultScheduledRange) {
        merged.defaultScheduledRange = {
          startDate: null,
          endDate: null,
          scheduledTime: null,
        };
      }
    }
    // If showScheduleDonation is explicitly true, keep it and the scheduled data

    return merged;
  };

  // create an async useEffect to fetch elements config
  const fetchElementsConfig = async ({ forceApply = false } = {}) => {
    const request = ++configRequestRef.current;
    const requestGeneration = activeConfigRef.current.generation;
    // Use formID from props as elementID for the API call
    let elementID = formID;
    // Store the original formID to check for stale responses
    const originalFormID = formID;
    setLoadError(false);

    // Previews use the authenticated editor's in-memory data, including saved drafts.
    if (previewMode) {
      if (previewData) setData(mergePreviewWithFetchedData({}, previewData));
      setBackendDataLoaded(true);
      return;
    }

    // In overlay mode, OverlayElement already fetched the data — use it directly to avoid duplicate fetch
    if (formDisplayMode === 'overlay' && previewData) {
      setData(previewData);
      setBackendDataLoaded(true);
      return;
    }

    if (elementID === 'NEW_FORM' || !elementID) {
      // Use helper function for development fallback (only in non-preview mode)
      // Updated to use a working elementID from the backend
      elementID = isDevelopment()
        ? '391e9af2-d97e-4e22-bc6f-b3ad4c2cf63b'
        : '318bfedb-b2ca-497a-8b7f-9d0cccf6fb0c';
    }

    // Use prop backendUrl if provided, otherwise fall back to env variable
    const backendUrl = propBackendUrl || getBackendUrl();
    if (!backendUrl) {
      console.error('❌ EmbedForm: Backend URL is not configured!');
      console.error('💡 Pass backendUrl as a prop or set VITE_ELEMENTS_BACKEND_URL in environment');
      // In preview mode, fall back to previewData so the form doesn't stay stuck on skeleton
      if (previewMode && previewData) {
        console.log('📋 [EmbedForm] No backend URL — using previewData as fallback');
        setData(previewData);
        setBackendDataLoaded(true);
      }
      return;
    }

    const apiUrl = buildGroupTicketV2ElementsConfigUrl({ backendUrl, elementID });

    try {
      // Use safeFetch with retry logic to handle network errors gracefully
      // Note: Using 1 retry here to avoid compounding with preview-mode retry below (max 4 attempts total)
      //
      const result = await safeFetchJSON(
        apiUrl,
        {},
        {
          context: 'Fetching form configuration',
          retries: 1,
          retryDelay: 1000,
        },
      );

      if (!result.ok) {
        throw new Error(result.error || 'API request failed');
      }

      const fetchedData = result.data;

      // A prior form generation cannot return after an A → B → A switch. For
      // overlapping requests for this SAME form, keep a valid older response
      // unless a newer successful response has already been applied.
      if (activeConfigRef.current.generation !== requestGeneration ||
          activeConfigRef.current.formID !== originalFormID ||
          activeConfigRef.current.configUrl !== configUrl ||
          (lastAppliedConfigRef.current?.generation === requestGeneration &&
            lastAppliedConfigRef.current.request > request)) {
        return;
      }
      lastAppliedConfigRef.current = { generation: requestGeneration, request };
      setLoadError(false);

      // Debug: Log scheduled donation data from API
      if (isDevelopment()) {
        console.log('📅 [EmbedForm] API Response - Scheduled Donation Fields:', {
          showScheduleDonation: fetchedData.showScheduleDonation,
          defaultScheduledRange: fetchedData.defaultScheduledRange,
          previewMode,
        });
      }

      // Remember the raw payload — never the preview-merged one — so a later
      // mount seeds from what the backend said (CON-21). Not in preview mode:
      // seeding is disabled there, so the entry could never be read.
      if (!previewMode) {
        rememberElementsConfig(apiUrl, fetchedData);
      }

      // A re-read checks whether what is on screen is still current. What it
      // must not do is hand the forms a NEW OBJECT IDENTITY for a configuration
      // they already have: every form re-initialises from `props.data`, so an
      // identical payload arriving mid-interaction would reset the donor's
      // amount, fund, frequency and custom-input answers for no reason. This
      // comparison also records the first response on a cold mount; otherwise
      // the null no-payload sentinel would disable equivalent-response checks
      // for that mount's entire lifetime. Identical: keep what is on screen,
      // unless RESET_ELEMENT explicitly asks to restore configured defaults.
      // Different: apply it — a donor must not give against a configuration the
      // nonprofit has changed, and correcting the form is the point of re-reading.
      // API JSON object key order is not semantic. Canonicalise objects at every
      // depth while leaving arrays untouched: amount tiers, funds, questions,
      // and their ordering are meaningful configuration values.
      const incoming = serializeConfigForComparison(fetchedData);
      if (!forceApply && incoming === renderedPayloadRef.current) {
        setBackendDataLoaded(true);
        return;
      }
      renderedPayloadRef.current = incoming;

      // If in preview mode, merge preview data with fetched data
      if (previewMode && previewData) {
        const mergedData = mergePreviewWithFetchedData(fetchedData, previewData);
        setData(mergedData);
      } else {
        setData(fetchedData);
      }

      // Mark backend data as loaded
      setBackendDataLoaded(true);
    } catch (error) {
      if (request !== configRequestRef.current ||
          activeConfigRef.current.generation !== requestGeneration ||
          activeConfigRef.current.formID !== originalFormID ||
          activeConfigRef.current.configUrl !== configUrl ||
          lastAppliedConfigRef.current?.generation === requestGeneration) return;
      setLoadError(true);
      if (isDevelopment()) {
        logApiError('EmbedForm', error, { elementID, apiUrl });
      }
      console.error('Failed to fetch form configuration:', error);

      // CRITICAL FIX: If we're in preview mode with an elementID and fetch fails, retry once after 1 second
      // This ensures that if the first load doesn't work and we have a valid elementID, we try again
      if (
        previewMode &&
        previewData &&
        previewData.elementID &&
        previewData.elementID !== 'preview' &&
        !previewModeRetryAttemptedRef.current
      ) {
        previewModeRetryAttemptedRef.current = true;
        setTimeout(() => {
          fetchElementsConfig();
        }, 1000);
      } else if (previewMode && previewData) {
        // All retries exhausted or no valid elementID — fall back to previewData
        // so the form doesn't stay stuck on skeleton forever
        console.log('📋 [EmbedForm] Backend fetch failed — using previewData as fallback');
        setData(previewData);
        setBackendDataLoaded(true);
      }
    }
  };

  // Separate effect for preview mode data updates
  // Convert preview data to JSON string for deep comparison
  const previewDataString = previewMode && previewData ? JSON.stringify(previewData) : null;

  useEffect(() => {
    if (previewMode && previewData) {
      // A type that already failed in this mount is not refetched on every edit.
      if (isLazyFormType(previewData.formType) && previewData.formType !== failedFormType) {
        loadFormType(previewData.formType, previewMode).catch(() => {});
      }
      // Use functional update to ensure we're working with the latest data state
      setConfigData((currentData) => {
        // For NEW forms: use previewData as base since there's no backend data
        // For EXISTING forms: use previewData immediately so the form renders,
        //   then merge with backend data once it arrives
        if (!currentData) {
          // Use previewData directly — the dashboard provides full form data
          // When backend data arrives, mergePreviewWithFetchedData will combine them
          return previewData;
        }

        // Merge preview data with existing fetched data for real-time updates
        const mergedData = mergePreviewWithFetchedData(currentData, previewData);
        return mergedData;
      });

      setElementID(previewData.elementID || formID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode, previewDataString, formID, backendDataLoaded]);

  // Initial setup effect — the CONFIGURATION half.
  //
  // Split out of the single effect this used to be (CON-21). The old one also
  // listed `isHostedPage`, `formDisplayMode` and `isDrawerOpen`, so opening the
  // hosted page's drawer sent the OTHER widget on that page — the desktop rail,
  // mounted and merely hidden by CSS — back to its placeholder and made it read
  // the network again for a payload it already held. A display flag flipping is
  // not a new configuration; only `formID` / the backend url are.
  useEffect(() => {
    // A mount that opened on a seed is ALREADY showing the real form for this
    // url. Clearing the ready flags would paint exactly the placeholder the seed
    // removes, so they are left alone — but the read below still happens.
    //
    // `!== null` is load-bearing, not defensive. A mount with NO seed carries a
    // null ref, and a mount that reads no url at all — the desktop overlay,
    // which is handed its payload by `OverlayElement` — has a null `configUrl`.
    // Comparing the two directly makes `null === null` true, and an earlier
    // version of this guard skipped `fetchElementsConfig` on that basis. That
    // call is the ONLY place the overlay branch (`setData(previewData)`) runs,
    // so it shipped a permanent placeholder on the desktop overlay money path
    // for one review round.
    const openedOnSeed =
      seededConfigUrlRef.current !== null && seededConfigUrlRef.current === configUrl;

    if (!openedOnSeed) {
      // Reset ready state when fetching new configuration
      setIsReady(false);
      setShowContent(false);

      // Reset navigation tracking when formID changes (new form loaded)
      hasUserNavigatedRef.current = false;
      previousEnableEmbedFormRef.current = null;
      previewModeRetryAttemptedRef.current = false;
      // The seed belonged to a url this mount has now left; coming back to it
      // must read rather than re-seed.
      seededConfigUrlRef.current = null;
      renderedPayloadRef.current = null;
    }

    setBackendDataLoaded(false); // Reset backend load flag

    fetchElementsConfig();
    // `configUrl` is the identity this effect is keyed on; `formID`,
    // `propBackendUrl` and `previewMode` are listed because they select which
    // payload a null url means (a preview with no backing element still has to
    // re-run its no-fetch path).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUrl, formID, propBackendUrl, previewMode]);

  // Initial setup effect — the DISPLAY half. Runs on every display change, as
  // the combined effect did, but touches no loading state.
  useEffect(() => {
    // Extract elementID/domID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const elementID = urlParams.get('elementID') || formID;
    const domID = urlParams.get('domID');

    setElementID(elementID);
    setDomID(domID);

    // If in hosted page context, hosted page preview, overlay mode, or drawer mode, skip detail page
    if (isHostedPage || formDisplayMode === 'overlay' || isDrawerOpen) {
      setCurrentPage('FORM');
    }
  }, [formID, isHostedPage, formDisplayMode, isDrawerOpen, propBackendUrl]);

  useEffect(() => {
    if (data) {
      // Detect if enableEmbedForm setting has changed
      const enableEmbedFormChanged =
        previousEnableEmbedFormRef.current !== null &&
        previousEnableEmbedFormRef.current !== data?.enableEmbedForm;

      // Update the previous value
      previousEnableEmbedFormRef.current = data?.enableEmbedForm;

      // Determine if we should auto-set the page
      // In preview mode, only auto-set on:
      // 1. Initial load (hasUserNavigatedRef.current === false)
      // 2. When enableEmbedForm toggle changes (enableEmbedFormChanged === true)
      const shouldAutoSetPage =
        !previewMode || !hasUserNavigatedRef.current || enableEmbedFormChanged;

      if (shouldAutoSetPage) {
        // Same resolver the first render used, so a seeded frame and the frame
        // after the response agree on the page (CON-21).
        setCurrentPage(
          resolveFormPage({ data, isHostedPage, hostedPagePreview, formDisplayMode, isDrawerOpen }),
        );

        // If enableEmbedForm changed, reset the navigation flag so future changes respect it again
        if (enableEmbedFormChanged && previewMode) {
          hasUserNavigatedRef.current = false;
        }
      }

      // Lock the container's CURRENT height before the placeholder is replaced,
      // so the swap glides on the drawer's own height dial instead of snapping
      // (CON-21). This is the same call a page switch makes, on the same ref,
      // and it must happen BEFORE the state change: it measures what is on
      // screen now and animates to what renders next. A no-op unless the
      // container is mounted and height transitions are enabled, so the
      // non-drawer placeholder — which shares its root with 13 other call sites
      // — is untouched.
      // `!isReady` IS "the placeholder is on screen": inside this branch `data`
      // is set and the palette resolves synchronously, so it is the only term of
      // `shouldShowSkeleton` still open. Without it this effect re-locks the
      // height on every later pass — the seeded payload being replaced by the
      // fetched one, for instance — which animates nothing and blocks a real
      // page transition through the hook's own in-progress guard.
      if (isDrawerOpen && !isReady) {
        prepareTransition();
      }

      // Signal that we're ready to render content with the correct page
      // This prevents flicker by keeping skeleton visible until page is determined
      setIsReady(true);

      // Next frame: trigger CSS fade-in animation by adding 'active' class
      // This creates a smooth transition from skeleton to first page
      requestAnimationFrame(() => {
        setShowContent(true);
      });
    }
  }, [data, hostedPagePreview, isHostedPage, formDisplayMode, previewMode]);

  // Notify parent when form is fully ready (data loaded, page determined, and content shown)
  useEffect(() => {
    if (isReady && showContent && formModuleReady && !showLoadError && onReady && !onReadyCalledRef.current) {
      onReadyCalledRef.current = true;
      onReady();
    }
  }, [isReady, showContent, formModuleReady, showLoadError, onReady]);

  // Reset onReady flag when formID changes (new form loaded)
  useEffect(() => {
    onReadyCalledRef.current = false;
  }, [formID]);

  // CON-63 mark 1 — form_visible, at the moment this component's placeholder
  // gives way to the form. This component renders in the widget, in the overlay
  // and inside the hosted page, so the mark names the ONE surface that stamps
  // the moment elsewhere: the hosted page has its own, later skeleton swap and
  // stamps it in `Base.jsx`. `skipSurfaces` is resolved against the surface the
  // page initialised as, not guessed from the URL or from whether a parent
  // happened to pass `onReady` — the overlay passes one and still owns its own
  // moment. The dashboard previewer is not a donor surface and is excluded by
  // `previewMode`. Capture only — a separate effect so the notify effect above
  // keeps its exact control flow.
  //
  // One more precondition, found by the adversarial review: `OverlayElement`
  // mounts TWO of these — a desktop pane that is `display:none` below `lg` and
  // a drawer pane that mounts when the donor taps Donate. Both become "ready"
  // on data; only one is on screen. The mark is once-per-page (module guard), so
  // the hidden pane must not claim it. The root node is `drawerContainerRef`
  // (already attached via `containerProps` — a second ref on the same element
  // would be overridden by the spread). A node inside a `display:none` subtree
  // has no client rects; such a pane waits on an IntersectionObserver and stamps
  // when it is first laid out (a merchant's collapsed tab opening, say) instead
  // of never. What this cannot see: a host page hiding the whole iframe with
  // `visibility:hidden`/`opacity:0` (the preloaded overlay) — boxes still exist,
  // and the iframe cannot read its host's styles. Stated, not hidden.
  useEffect(() => {
    if (previewMode || formVisibleMarkedRef.current) return;
    if (!(isReady && showContent && areBrandColorsReady && formModuleReady) || showLoadError) return;
    const stamp = () => {
      if (formVisibleMarkedRef.current) return;
      formVisibleMarkedRef.current = true;
      markJourney(JOURNEY_MARKS.FORM_VISIBLE, {}, { skipSurfaces: ['hosted-page'] });
    };
    let el = null;
    try {
      el = drawerContainerRef.current;
      if (!el || el.getClientRects().length > 0) {
        stamp();
        return undefined;
      }
    } catch {
      stamp();
      return undefined;
    }
    // Laid out later: stamp on first intersection. No observer → stamp now (old behaviour).
    let observer = null;
    try {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.width > 0)) {
          stamp();
          observer?.disconnect();
        }
      });
      observer.observe(el);
    } catch {
      stamp();
      return undefined;
    }
    return () => observer?.disconnect();
  }, [isReady, showContent, areBrandColorsReady, formModuleReady, showLoadError, previewMode]);

  // Show loading UI until data is loaded AND correct page is determined AND brand colors are ready
  // This prevents flicker by keeping skeleton visible during page determination
  // In preview mode, previewData is used immediately so we don't need to wait for backend
  const shouldShowSkeleton = !data || !isReady || !areBrandColorsReady || !formModuleReady;

  if (showLoadError) {
    return <p role="status" className="p-6 text-center text-[color:var(--cs-text-secondary)]">This form is unavailable.</p>;
  }
  // The widget's outer container. Declared once and used by BOTH the
  // placeholder branch and the form branch so React reconciles them onto the
  // SAME DOM node — which is what keeps `drawerContainerRef` attached across the
  // swap and lets the height animate rather than snap (CON-21).
  //
  // Sharing the props is necessary but NOT sufficient: React reconciles by
  // element TYPE at a position, so anything wrapping one branch and not the
  // other replaces the node anyway. `withBrandScope` below is therefore applied
  // to both returns rather than to the form alone (CON-157).
  const containerProps = {
    ref: drawerContainerRef,
    id: `${elementID}-CS-embed-form`,
    className: `page-container embed-form-responsive-container ${isDrawerOpen ? 'drawer-height-animate' : ''}`,
    'data-mode': formDisplayMode === 'overlay' ? 'overlay' : isDrawerOpen ? 'drawer' : 'dynamic',
    style: {
      // CON-160: a real floor, not `fit-content`.
      //
      // `fit-content` as a min-height is close to a no-op — it lets the
      // container be as short as its content, including when its content is
      // momentarily nothing. The capture caught exactly that: at the
      // placeholder -> form swap the container went 600px -> 0px -> 600px in
      // consecutive frames, which is the flash a donor sees and the bounce a
      // host page makes. The same floor the skeleton and the card already use
      // (`DYNAMIC_FORM_MIN_HEIGHT`) stops the height reaching zero at all.
      //
      // CON-529 F9: in dynamic mode the floor holds only while the placeholder
      // shows. Once the form is in, the card hugs its content (Paper), and the
      // container's `min-height` transition (forms.css
      // `.page-container[data-mode=dynamic]`) eases the swap down to it instead
      // of dropping to a zero frame. The drawer keeps its floor: its height lock
      // (CON-21) measures against it, and without it the drawer body collapsed
      // under an open question menu (EMB-012 stress spec).
      minHeight:
        formDisplayMode === 'overlay' || (!shouldShowSkeleton && !isDrawerOpen)
          ? undefined
          : DYNAMIC_FORM_MIN_HEIGHT,
      height: formDisplayMode === 'overlay' ? undefined : 'auto',
    },
  };

  // ONE brand scope, ABOVE the placeholder/form split, for the reason the
  // comment on `containerProps` gives: wrapping only the form branch changes the
  // root element type on the placeholder→form swap, so React destroys the
  // container and mounts a fresh one, `drawerContainerRef` re-attaches to a new
  // node, and the height transition has no prior height to animate from.
  //
  // The wrapper is conditioned ONLY on `mountBrandProvider`, which is a constant
  // prop. Conditioning it on `configuredBrandColor` — which legitimately starts
  // null and fills in once the config or the URL params land — would just move
  // the same node replacement onto that flip instead. The COLOUR is still gated,
  // so an organisation with no configured colour writes no brand tokens, exactly
  // as before.
  //
  // CON-313: the display-currency provider sits here for the same reason, and
  // unconditionally, so both returns get the same element at the same position.
  // It renders no DOM node. Off in either preview: the dashboard previewer and a
  // hosted-page preview must never fetch rates or emit CURRENCY_SHOWN.
  // CON-750: it publishes its value so the bar and donor list beside the form
  // (FollowDisplayCurrency on the hosted page and the overlay) show the same.
  const currencyOff = previewMode || hostedPagePreview;
  const withBrandScope = (node) => (
    <DisplayCurrencyProvider
      config={currencyOff ? null : data}
      enabled={!currencyOff}
      scopeRef={drawerContainerRef}
      publish={!currencyOff}
    >
      {mountBrandProvider ? (
        <BrandColorProvider
          brandColors={configuredBrandColor ? generatedBrandColors : undefined}
          scope={drawerContainerRef}
        >
          {node}
        </BrandColorProvider>
      ) : node}
    </DisplayCurrencyProvider>
  );

  if (shouldShowSkeleton) {
    // The frame for this form type: the preview's, or the response's when it
    // landed before the type's chunk; STANDARD_FORM until either is known.
    // With a configuration in hand the frame is sized to this form and opens
    // on the page the form will open on (a detail page draws the detail card).
    const skeletonConfig = previewData || data || null;
    const skeletonFormType = skeletonConfig?.formType || 'STANDARD_FORM';
    const skeletonPage = skeletonConfig
      ? resolveFormPage({ data: skeletonConfig, isHostedPage, hostedPagePreview, formDisplayMode, isDrawerOpen })
      : 'FORM';
    const skeletonProps = {
      formType: skeletonFormType,
      config: skeletonConfig,
      page: skeletonPage,
      isHostedPage: isHostedPage || hostedPagePreview,
      elementID,
      domID,
      brandColors,
      isDrawerOpen,
      formDisplayMode,
    };
    const skeleton = <FormSkeleton {...skeletonProps} />;

    // CON-160: the placeholder and the form go into the SAME container div, in
    // dynamic mode as well as the drawer.
    //
    // WHAT THIS FIXES, measured (`scratch-data/con-160/flash-capture.mjs`).
    // Returning the skeleton as its own root meant placeholder and form were
    // different DOM nodes, so the swap was a remount — and for at least one
    // frame the new container rendered with `.page` NOT yet `active`, which is
    // opacity 0 and height 0. The capture caught exactly that: container height
    // 600 -> 0 -> 600, opacity 1 -> 0, in the two frames around the swap. That
    // collapse is the flash a donor sees and the bounce the host page makes.
    //
    // Sharing the node means the container keeps its laid-out height across the
    // swap, and `drawerContainerRef` is attached from the first frame rather
    // than from the form's — which is what lets CON-21's height lock hold
    // across a dynamic swap too.
    //
    // `bare` is why this is safe now and was not before: `FormSkeleton`'s
    // non-drawer branch renders its own `page-container`, and wrapping it would
    // have nested two. The drawer branch has no container of its own, so it
    // does not need the prop.
    //
    // MERGE NOTE (dev, 2026-09-15): `withBrandScope` now wraps BOTH this and
    // the form, so the provider no longer appears only at the swap. That was
    // the remaining cause of the remount CON-160's record named and left to a
    // decision — it is settled on dev, and the two changes compose: same
    // wrapper, same container node, no remount.
    return withBrandScope(
      <div {...containerProps}>
        {isDrawerOpen ? skeleton : <FormSkeleton {...skeletonProps} bare />}
      </div>
    );
  }

  // Apply schedule config override from element prefills (URL builder).
  // Check both camelCase (from elementPrefills) and lowercase (from useUrlPrefill, which lowercases all keys).
  // popups.js passes prefillParams as cs_* URL query params on the overlay iframe.
  let effectiveData = data;
  const scheduleOverrideRaw =
    urlOverrides?.defaultScheduledRangeOverride || urlOverrides?.defaultscheduledrangeoverride;

  if (scheduleOverrideRaw) {
    try {
      const scheduleOverride =
        typeof scheduleOverrideRaw === 'string'
          ? JSON.parse(scheduleOverrideRaw)
          : scheduleOverrideRaw;
      if (scheduleOverride && typeof scheduleOverride === 'object') {
        effectiveData = { ...data, defaultScheduledRange: scheduleOverride };
      }
    } catch {
      // Invalid JSON — use original data
    }
  }

  if (!previewMode && paymentsEnabled === false) {
    effectiveData = { ...effectiveData, paymentsEnabled: false };
  }

  const form = (
    <div {...containerProps}>
      {/* Detail Page - skipped wherever something else shows its content (see rendersDetailPage) */}
      {rendersDetailPage && (
        <FormSurfacePage
          active={currentPage === 'DETAIL_PAGE' && showContent}
          direction={pageDirection}
          formDisplayMode={formDisplayMode}
        >
          <EmbedFormDetailPage
            data={effectiveData}
            setPage={smoothSetCurrentPage}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            isDrawerOpen={isDrawerOpen}
            elementID={elementID}
            previewMode={previewMode}
            onPreviewSubmit={onPreviewSubmit}
            domID={domID}
            headingLevel={headingLevel}
          />
        </FormSurfacePage>
      )}

      {/* Form Page - Always rendered, conditionally displays form type */}
      <FormSurfacePage
        active={currentPage === 'FORM' && showContent}
        direction={pageDirection}
        formDisplayMode={formDisplayMode}
      >
        {data.formType === 'EMBED_FORM' && (
          <StandardForm
            data={effectiveData}
            fetchElementsConfig={fetchElementsConfig}
            isDrawerOpen={isDrawerOpen}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            setPage={smoothSetCurrentPage}
            currentPage={currentPage}
            hostedPagePreview={hostedPagePreview}
            enableEmbedForm={data.enableEmbedForm}
            previewMode={previewMode}
            onPreviewSubmit={onPreviewSubmit}
            urlOverrides={standardFormOverrides}
            formDisplayMode={formDisplayMode}
            hideFAQ={hideFAQ}
            onSelectionChange={onSelectionChange}
            carriedSelection={carriedSelection}
          />
        )}
        {data.formType === 'CROWDFUNDING_FORM' && (
          <FormTypeComponent
            data={effectiveData}
            fetchElementsConfig={fetchElementsConfig}
            isDrawerOpen={isDrawerOpen}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            setPage={smoothSetCurrentPage}
            currentPage={currentPage}
            hostedPagePreview={hostedPagePreview}
            enableEmbedForm={data.enableEmbedForm}
            previewMode={previewMode}
            onPreviewSubmit={onPreviewSubmit}
            urlOverrides={urlOverrides}
            formDisplayMode={formDisplayMode}
            hideFAQ={hideFAQ}
          />
        )}
        {data.formType === 'PRODUCT' && (
          <FormTypeComponent
            data={effectiveData}
            fetchElementsConfig={fetchElementsConfig}
            isDrawerOpen={isDrawerOpen}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            setPage={smoothSetCurrentPage}
            currentPage={currentPage}
            hostedPagePreview={hostedPagePreview}
            enableEmbedForm={data.enableEmbedForm}
            previewMode={previewMode}
            onPreviewSubmit={onPreviewSubmit}
            urlOverrides={urlOverrides}
            formDisplayMode={formDisplayMode}
            hideFAQ={hideFAQ}
          />
        )}
        {data.formType === 'EVENT' && (
          <FormTypeComponent
            data={effectiveData}
            fetchElementsConfig={fetchElementsConfig}
            isDrawerOpen={isDrawerOpen}
            brandColors={brandColors}
            generatedBrandColors={generatedBrandColors}
            setPage={smoothSetCurrentPage}
            currentPage={currentPage}
            hostedPagePreview={hostedPagePreview}
            enableEmbedForm={data.enableEmbedForm}
            previewMode={previewMode}
            onPreviewSubmit={onPreviewSubmit}
            urlOverrides={urlOverrides}
            formDisplayMode={formDisplayMode}
            hideFAQ={hideFAQ}
          />
        )}
      </FormSurfacePage>

      {/* Debug: Show when no form matches */}
      {currentPage === 'FORM' &&
        !['EMBED_FORM', 'CROWDFUNDING_FORM', 'PRODUCT', 'EVENT'].includes(data.formType) && (
          <div
            style={{
              padding: '20px',
              border: '2px solid red',
              backgroundColor: '#ffe6e6',
              margin: '20px',
            }}
          >
            <h3>🚨 Debug: No Form Type Match</h3>
            <p>
              <strong>currentPage:</strong> {currentPage}
            </p>
            <p>
              <strong>data.formType:</strong> &quot;{data.formType}&quot;
            </p>
            <p>
              <strong>data.elementType:</strong> &quot;{data.elementType}&quot;
            </p>
            <p>
              <strong>Available form types:</strong> EMBED_FORM, CROWDFUNDING_FORM, PRODUCT, EVENT
            </p>
          </div>
        )}
    </div>
  );

  return withBrandScope(form);
}

export default EmbedForm;
